import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Box,
  CircularProgress,
  ThemeProvider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Header from "src/components/header/header";
import theme from "src/styles/mainstyle";
import Footer from "src/components/footer/footer";
import ContentBox from "src/components/ContentBox/ContentBox";
import NotFoundPage from "./MainPage/Components/NotFound";

// Webpack: require all .md files in ../articles
const req = require.context("../media/articles", true, /\.md$/);
const LANGUAGES = {
  eng: { label: "English", flag: "🇬🇧" },
  sv: { label: "Svenska", flag: "🇸🇪" },
  fr: { label: "Français", flag: "🇫🇷" },
  de: { label: "Deutsch", flag: "🇩🇪" },
  // Add more as needed
};

export default function ArticlePage() {
  const { slug, lang } = useParams();
  const [currentLang, setCurrentLang] = useState(lang || "eng");
  const [content, setContent] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!slug || !currentLang) return;
    const fileName = `./${slug}/${currentLang}.md`;
    if (req.keys().includes(fileName)) {
      setContent(undefined);
      const file = req(fileName);
      fetch((file as string) || (file as { default: string }).default)
        .then((res) => res.text())
        .then(setContent)
        .catch(() => setContent(null));
    } else {
      setContent(null);
    }
  }, [slug, currentLang]);

  if (content === null) return <NotFoundPage />;
  if (content === undefined) return <CircularProgress />;

  // Find available languages for this article
  const availableLangs = req
    .keys()
    .filter((key) => key.startsWith(`./${slug}/`))
    .map((key) => key.split("/")[2].replace(".md", ""));

  return (
    <>
      <ThemeProvider theme={theme}>
        <Header />
        <Box sx={{ p: 3 }} />
        <ContentBox>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                labelId="lang-select-label"
                id="lang-select" // <-- add this!
                value={currentLang}
                label=""
                onChange={(e) => setCurrentLang(e.target.value)}
                sx={{ fontWeight: "bold" }}
              >
                {availableLangs.map((l) => (
                  <MenuItem key={l} value={l}>
                    <span style={{ marginRight: 8 }}>
                      {(
                        LANGUAGES as Record<
                          string,
                          { label: string; flag: string }
                        >
                      )[l]?.flag || "🏳️"}
                    </span>
                    {(
                      LANGUAGES as Record<
                        string,
                        { label: string; flag: string }
                      >
                    )[l]?.label || l.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <ReactMarkdown
            components={{
              img: ({ node, ...props }) => (
                <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                  <img
                    {...props}
                    style={{
                      maxWidth: "100%",
                      width: "100%",
                      height: "auto",
                      maxHeight: "60vh", // never taller than 40% of viewport
                      objectFit: "contain",
                      display: "block",
                      borderRadius: 8,
                      // boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                    alt={props.alt}
                  />
                </Box>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </ContentBox>
        <Footer />
      </ThemeProvider>
    </>
  );
}
